'use client';

import { useState, useEffect, useCallback } from 'react';
import { useT } from '@/components/i18n-provider';
import { RotateCcw, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

export interface RevisionItem {
  id: string;
  agentId: string;
  version: number;
  changeMessage: string | null;
  createdAt: string;
}

interface AgentRevisionsProps {
  agentIdOrName: string;
  activeConfigId: string | null;
  onActivated?: () => void;
}

const PAGE_SIZE = 5;

export function AgentRevisions({
  agentIdOrName,
  activeConfigId,
  onActivated,
}: AgentRevisionsProps) {
  const t = useT();
  const [revisions, setRevisions] = useState<RevisionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const fetchPage = useCallback(async (pageOffset: number) => {
    try {
      setLoading(true);
      const res = await fetch(
        `/api/agents/${encodeURIComponent(agentIdOrName)}/revisions?limit=${PAGE_SIZE}&offset=${pageOffset}`
      );
      if (res.ok) {
        const data = await res.json();
        setRevisions(data.revisions ?? []);
        setTotal(data.total ?? 0);
        setOffset(pageOffset);
      }
    } catch {
      setRevisions([]);
    } finally {
      setLoading(false);
    }
  }, [agentIdOrName]);

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage, activeConfigId]);

  const handleActivate = async (revisionId: string) => {
    if (revisionId === activeConfigId) return;
    setActivatingId(revisionId);
    try {
      const res = await fetch(
        `/api/agents/${encodeURIComponent(agentIdOrName)}/revisions/${revisionId}/activate`,
        { method: 'POST' }
      );
      if (res.ok) onActivated?.();
    } finally {
      setActivatingId(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  if (loading && revisions.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner size="lg" tone="muted" />
      </div>
    );
  }

  if (revisions.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('agentRevisions.none')}</p>;
  }

  function formatDate(raw: string) {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return (
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' \u00b7 ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    );
  }

  return (
    <div className="space-y-2">
      {revisions.map((rev) => (
        <div
          key={rev.id}
          className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
            rev.id === activeConfigId ? 'border-green-500/30 bg-green-500/5' : 'border-border'
          }`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">v{rev.version}</span>
              {rev.id === activeConfigId && (
                <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">
                  Active
                </span>
              )}
              {rev.changeMessage && rev.changeMessage !== '—' && (
                <span className="text-xs text-muted-foreground truncate">{rev.changeMessage}</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {formatDate(rev.createdAt)}
            </p>
          </div>
          {rev.id === activeConfigId ? (
            <Check className="h-4 w-4 text-green-500 shrink-0" />
          ) : (
            <button
              type="button"
              onClick={() => handleActivate(rev.id)}
              disabled={activatingId === rev.id}
              className="text-xs text-primary hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors flex items-center gap-1 shrink-0"
            >
              {activatingId === rev.id ? (
                <Spinner size="sm" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              Restore
            </button>
          )}
        </div>
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={() => fetchPage(offset - PAGE_SIZE)}
            disabled={offset === 0 || loading}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 flex items-center gap-0.5"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Prev
          </button>
          <span className="text-[11px] text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => fetchPage(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total || loading}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 flex items-center gap-0.5"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
